/**
 * Utility for handling country flag images with graceful fallback
 *
 * Provides a fallback placeholder when flagcdn.com or other CDN services fail.
 * The placeholder is a simple gray square data URI that can be used inline.
 */

/**
 * A simple gray placeholder as a data URI for when flag images fail to load.
 * This is a 20x15 gray rectangle to match typical flag dimensions.
 */
export const FLAG_PLACEHOLDER =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="20" height="15" viewBox="0 0 20 15"%3E%3Crect width="20" height="15" fill="%23D1D5DB"/%3E%3C/svg%3E';

/**
 * Gets the URL for a country flag from flagcdn.com
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'US', 'CA')
 * @param size - Width of the flag image (default: 20)
 * @returns The URL for the flag image
 */
export const getFlagUrl = (countryCode: string, size: number = 20): string => {
  return `https://flagcdn.com/w${size}/${countryCode.toLowerCase()}.png`;
};

/**
 * Gets the srcSet for responsive flag images
 * @param countryCode - ISO 3166-1 alpha-2 country code
 * @param size - Base width of the flag image (default: 20)
 * @returns The srcSet string for 1x and 2x displays
 */
export const getFlagSrcSet = (countryCode: string, size: number = 20): string => {
  const size2x = size * 2;
  return `${getFlagUrl(countryCode, size)} 1x, ${getFlagUrl(countryCode, size2x)} 2x`;
};

/**
 * Handles flag image load errors by replacing the src with a placeholder
 * Use this as an onError handler for flag img elements
 *
 * @example
 * ```tsx
 * <img
 *   src={getFlagUrl('US')}
 *   onError={handleFlagImageError}
 *   alt="United States"
 * />
 * ```
 */
export const handleFlagImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  const img = e.currentTarget;
  // Only replace if not already showing placeholder
  if (!img.src.startsWith('data:')) {
    img.src = FLAG_PLACEHOLDER;
    // Remove srcSet to prevent further retries
    img.removeAttribute('srcset');
  }
};
